import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { useListRecordsQuery, useMeQuery } from '@platform/state/api';
import { firstRecordArgs } from './firstRecordArgs';
import { formSingular } from './formSingular';
import type { ISettingsFormHeadProps } from './ISettingsFormHeadProps';

/**
 * Шапка редактора форми (прототип v4 pgCustForm): повернення контексту («← Модуль», «← Видатки» чи «← Форми»), «Форма «Однина»» 22px і
 * підрядок «Модуль · …»: на рівні «Глобально» — «Глобальна форма — для всіх робочих просторів без власної форми» (без «Відкрити запис»), у
 * просторі — «Форма «Робочий простір» · Простір», «Успадковано з глобальної форми…» або «Стандартний макет…»; праворуч «Відкрити запис»
 * (запис типу з найменшим номером, як records[0] прототипу) і для власної форми рівня — червона кнопка скидання з підтвердженням: «Скинути до
 * стандартного» або, у просторі з глобальною формою, «Використати глобальну форму»; її немає, коли скидання нічого не змінило б (незмінена
 * засіяна картка парламенту — «Форма «Робочий простір»», рішення 2 етапу 10).
 */
export function SettingsFormHead({ ws, module, rt, name, editor, links }: ISettingsFormHeadProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings(module.module.key);
  const { data: me } = useMeQuery();
  const { global, source } = editor;
  const { data } = useListRecordsQuery(firstRecordArgs(ws, rt.key), { skip: global });
  const first = global ? undefined : data?.items[0];
  const singular = formSingular(strings, rt);
  const wsName = me?.workspaces.find((w) => w.id === ws)?.name ?? '';
  // Дочірня таблиця (?type=) у прототипі — окремий модуль: підрядок називає її, а не модуль-власника.
  const owner = rt.main ? name : strings.entity(rt.key, 'name', rt.name) || name;
  const sub = global ? t('modSettings.form.subGlobal', { name: owner })
    : source === 'ws' ? t('modSettings.form.subWs', { name: owner, ws: wsName })
      : t(source === 'global' ? 'modSettings.form.subInherited' : 'modSettings.form.sub', { name: owner });
  const own = source === (global ? 'global' : 'ws');
  const confirm = t(global ? 'modSettings.form.resetGlobalConfirm' : 'modSettings.form.resetConfirm', { name: singular.toLocaleLowerCase() });
  const reset = () => { if (window.confirm(confirm)) editor.reset(); };
  return (
    <div className="st-fhead">
      <Link className="st-back" to={links.backTo}>{links.backLabel}</Link>
      <div className="st-head__row st-head__row--end">
        <div className="st-fhead__text">
          <h1 className="st-head__title">{t('modSettings.form.title', { name: singular })}</h1>
          <div className="st-head__sub">{sub}</div>
        </div>
        <div className="st-fhead__actions">
          {first && <Link className="st-head__btn" to={routes.record(module.module.key, rt.key, first.id)}>{t('modSettings.form.openRecord')}</Link>}
          {own && editor.canEdit && editor.resettable && (
            <button type="button" className="st-head__btn st-head__btn--danger" onClick={reset}>
              {t(!global && editor.globalForm ? 'modSettings.form.useGlobal' : 'modSettings.form.reset')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
